<template>
  <div class="text-center mx-auto mb-5" :style="{ 'max-width': '587px' }">
    <h2 class="font36 gray800 text-center fw700 mt-5">Update your DoveDaily</h2>
    <p class="gray500 text-center font16">
      Use this feature to keep track and inform the others about your daily mood. You can also keep track of your treatment progress and effectiveness (only visible to you and DoveMed researchers).
    </p>

    <!-- MOOD -->
    <div class="moodBox mt-5 py-3 px-4 shadow rounded">
      <h6 class="text-left fw700 gray800 font18">
        Mood
        <span v-if="moodDisabled" class="fw300 gray500 font18"> (Already updated for today) </span>
      </h6>
      <div class="d-flex justify-between" :style="{ gap: '8px' }">
        <div
          v-for="mood in moods"
          :key="mood"
          :class="`br8 py-2 px-3 flex-grow-1 cp d-flex align-items-center eachMoodBtn`"
          :style="getMoodColor(mood)"
          @click="() => (clickedMood = clickedMood === mood ? undefined : mood)"
        >
          <img :src="require(`../../assets/emojis/${mood}-32.png`)" :alt="mood" />
          <p class="ml-2 gray700 font12 fw700 text-capitalize">{{ mood }}</p>
        </div>
      </div>
      <div class="feelBox mt-3" v-if="clickedMood">
        <p class="fw700 gray500 font16 text-left">
          Why do you feel this way?
          <span class="fw300 gray500 font16"> (Optional) </span>
        </p>
        <a-textarea class="mt-1 dovedaily-textarea" :placeholder="'Describe how you feel...'" :rows="8" v-model="moodFeel" :maxlength="maxCharacters" />
        <p class="text-right font12 gray400">{{ moodFeel.length }}/{{ maxCharacters }}</p>
        <button :style="{ border: 'none' }" class="d-flex p1224 br6 bg-gray200 gray600 font14 ml-auto cp mt-3" @click="saveBtn('mood', moodFeel)">Save</button>
      </div>
    </div>

    <!-- MODAL FIELDS -->
    <div class="mt-3 py-3 px-4 shadow rounded" v-for="modals in modalValues" :key="modals.id">
      <div v-if="getLoader(modals.name)" class="d-flex justify-content-center my-3">
        <div>
          <div class="spinner-border spinner-class"></div>
          <div>Loading {{ modals.name }}...</div>
        </div>
      </div>
      <template v-else>
        <div class="d-flex mb-2">
          <h6 class="flex-grow-1 fw700 gray800 font18 text-left my-auto text-capitalize">
            {{ modals.name }}
          </h6>
          <font-awesome-icon
            v-if="getList(modals.name).length"
            :icon="['fas', 'eye']"
            class="eyeIcon cp"
            :style="{ color: getPrimaryColor() }"
            @click="() => $refs.ViewDetailsModal.show(modals.name, getList(modals.name), modals.id)"
          />
          <font-awesome-icon :icon="['far', 'plus']" class="plusIcon cp" :style="{ color: getPrimaryColor() }" @click="handleOpenModal(modals.name, 'add')" />
        </div>
        <div v-if="!getList(modals.name).length" class="br8 font16 p-3 bg-gray100 fw700 gray400 w-100 text-left">You haven’t added any {{ modals.name }} yet.</div>
        <div v-else class="d-flex gap10">
          <div class="br6 bg-gray200 fw700 gray600 font14 cp p1224" v-for="option in modals.options" :key="option" @click="handleStopTakingModal(modals.id, option, getList(modals.name))">
            {{ option }}
          </div>
        </div>
      </template>
    </div>

    <!-- INPUT FIELDS -->
    <div class="mt-3 d-flex py-3 px-4 shadow rounded" v-for="inputs in inputValues" :key="inputs.id">
      <h6 class="font18 flex-grow-1 fw700 gray800 text-capitalize text-left my-auto">
        {{ inputs.name }}
      </h6>
      <a-input class="dovedailyInput" v-model="inputs.val" :suffix="inputs.suffix" placeholder="0" type="number" />
      <div class="p1224 br6 bg-gray200 gray600 font14 cp" @click="saveBtn(inputs.id, inputs.val)">Save</div>
    </div>

    <!-- PROCEDURES -->

    <div class="mt-3 py-3 px-4 shadow rounded">
      <div v-if="proLoading" class="d-flex justify-content-center my-3">
        <div>
          <div class="spinner-border spinner-class"></div>
          <div>Loading Procedures...</div>
        </div>
      </div>
      <template v-else>
        <div class="d-flex">
          <h6 class="font18 flex-grow-1 fw700 gray800 text-capitalize text-left my-auto">Procedure</h6>
          <font-awesome-icon
            v-if="procedures.length"
            :icon="['fas', 'eye']"
            class="eyeIcon cp"
            :style="{ color: getPrimaryColor() }"
            @click="() => $refs.ViewDetailsModal.show('procedures', procedures, 'procedure')"
          />
          <font-awesome-icon :icon="['far', 'plus']" class="plusIcon cp" :style="{ color: getPrimaryColor() }" @click="handleOpenModal('procedures', 'add')" />
        </div>
        <div v-if="!procedures.length" class="br8 font16 p-3 bg-gray100 fw700 gray400 w-100 text-left">You haven’t added any procedures yet.</div>
      </template>
    </div>

    <!-- MODALS -->
    <MedicationModal ref="MedicationModal" />
    <ActivityModal ref="ActivityModal" />
    <ComplimentaryTherapyModal ref="ComplimentaryTherapyModal" />
    <NutritionModal ref="NutritionModal" />
    <ProcedureModal ref="ProcedureModal" />
    <ViewDetailsModal ref="ViewDetailsModal" :handleModal="handleModal" :handleDeleteMed="handleDeleteMed" />
    <StopTakingModal ref="StopTakingModal" />
  </div>
</template>

<script>
import NutritionModal from "../Modals/dovedaily/Nutrition.vue"
import ActivityModal from "../Modals/dovedaily/Activity.vue"
import ProcedureModal from "../Modals/dovedaily/Procedure.vue"
import MedicationModal from "../Modals/dovedaily/Medication.vue"
import ComplimentaryTherapyModal from "../Modals/dovedaily/ComplimentaryTherapy.vue"
import ViewDetailsModal from "../Modals/dovedaily/ViewDetails.vue"
import StopTakingModal from "../Modals/dovedaily/StopTaking.vue"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faEye } from "@fortawesome/pro-solid-svg-icons"
import { faPlus } from "@fortawesome/pro-regular-svg-icons"
import CreateNewPostModal from "../Modals/CreateNewPost.vue"
import { RepositoryFactory } from "../../repositories/RepositoryFactory"
import axios from "axios"

const PostRepository = RepositoryFactory.get("posts")
const UserRepository = RepositoryFactory.get("user")
library.add(faEye, faPlus)

export default {
  name: "UpdateDoveDaily",
  components: {
    CreateNewPostModal,
    ComplimentaryTherapyModal,
    NutritionModal,
    ActivityModal,
    ProcedureModal,
    MedicationModal,
    ViewDetailsModal,
    StopTakingModal,
  },
  data() {
    return {
      showModal: false,
      clickedMood: undefined,
      moods: ["good", "okay", "bad"],
      maxCharacters: 2000,
      moodFeel: "",
      procedures: [],
      medications: [],
      activities: [],
      CTs: [],
      nutritions: [],
      inputValues: [
        { name: "Weight", val: undefined, id: "weight", suffix: "lb" },
        { name: "Sleep", val: undefined, id: "sleep", suffix: "h" },
        { name: "Heart Rate", val: undefined, id: "heart_rate", suffix: "bpm" },
        {
          name: "Body Temperature",
          val: undefined,
          id: "temperature",
          suffix: "°F",
        },
      ],
      modalValues: [
        {
          name: "medications",
          id: "Medication",
          options: ["Fully taken", "Partially taken", "Not taken"],
        },
        {
          name: "activities",
          id: "Activity",
          options: ["As per plan", "Not as per plan"],
        },
        {
          name: "complimentary therapies",
          id: "Complimentary Therapy",
          options: ["As per plan", "Not as per plan"],
        },
        {
          name: "nutritions",
          id: "Nutrition",
          options: ["As per plan", "Partially as per plan", "Not as per plan"],
        },
      ],
      medLoading: false,
      actLoading: false,
      nutLoading: false,
      ctLoading: false,
      proLoading: false,
      moodDisabled: false,
      // g_auth_code: null,
      // client_id: "703299315924-8e04nejnfboidhic7k0vhu42e8056kt7.apps.googleusercontent.com",
      // client_id: "101951622305-r97l0t8dvq817olgmshsrjrekmuna8k1.apps.googleusercontent.com",
      // client_secret: "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",
      // client_secret: "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",
      // auth_code: null,
      a_token: "",
    }
  },
  async created() {
    // console.log("process.env.SSO_BASE_URL ->", process.env.SSO_BASE_URL)

    if (this.authUser.user.role !== "patient" && this.authUser.user.role !== "caregiver") {
      this.$notification.error({ message: "Error", description: "You don't have access to Dovedaily" })
      this.$router.push({ name: "ActivityHome" })
      return
    }

    try {
      this.nutLoading = true
      this.ctLoading = true
      this.actLoading = true
      this.proLoading = true
      this.medLoading = true

      const response = await UserRepository.getDovedaily(this.currentCircle.id)
      console.log("dovedaily ->", response.data.data)
      if (response.data.data[0] && response.data.data[0].mood) {
        this.moodDisabled = true
      }
      if (response.data.data && response.data.data.length) {
        this.inputValues = this.inputValues.map(v => {
          return { ...v, val: response.data.data[0][v.id] || undefined }
        })
      }

      const medResponse = await UserRepository.getTreatments(this.authUser.user, this.currentCircle.id, "Medication")
      this.medications = medResponse.data.data
      this.medLoading = false

      const activityResponse = await UserRepository.getTreatments(this.authUser.user, this.currentCircle.id, "Activity")
      this.activities = activityResponse.data.data
      this.actLoading = false

      const CTResponse = await UserRepository.getTreatments(this.authUser.user, this.currentCircle.id, "Non Medication")
      this.CTs = CTResponse.data.data
      this.ctLoading = false

      const nutritionResponse = await UserRepository.getTreatments(this.authUser.user, this.currentCircle.id, "Nutrition")
      this.nutritions = nutritionResponse.data.data
      this.nutLoading = false

      const proceduresResponse = await UserRepository.getTreatments(this.authUser.user, this.currentCircle.id, "Procedure")
      this.procedures = proceduresResponse.data.data
      this.proLoading = false

      const gFitAllowed = JSON.parse(localStorage.getItem("gFitInfo") || '{"allowed":false}').allowed
      if (gFitAllowed) {
        // await this.getToken()
        // this.getGFit("com.google.sleep.segment")
        // this.getGFit("com.google.body.temperature")
        // this.getGFit("com.google.heart_rate.bpm")
        // this.getGFit("com.google.weight")
      }
    } catch (e) {
      this.$notification.error({ message: "Error", description: "Could not fetch the details. PLEASE REFRESH" })
      this.nutLoading = false
      this.ctLoading = false
      this.actLoading = false
      this.proLoading = false
      this.medLoading = false
    }
  },
  methods: {
    // async getDataSources() {
    //   try {
    //     const dataSourcesRes = await axios.get("https://www.googleapis.com/fitness/v1/users/me/dataSources", { headers: { Authorization: `Bearer ${this.a_token}` } })
    //     console.log("DSource ->", dataSourcesRes.data)
    //   } catch (e) {
    //     console.log(e)
    //   }
    // },
    // async getDataSets() {
    //   const today = new Date()
    //   try {
    //     const dataSetsRes = await axios.get("https://www.googleapis.com/fitness/v1/users/me/dataset", {
    //       headers: { Authorization: `Bearer ${this.a_token}` },
    //       params: { dataTypeName: "com.google.sleep.segment", startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2).toISOString(), endTime: today.toISOString() },
    //     })
    //     console.log("DSets ->", dataSetsRes.data)
    //   } catch (e) {
    //     console.log(e)
    //   }
    // },
    connectGoogle() {
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/fitness.activity.read+https://www.googleapis.com/auth/fitness.body.read+https://www.googleapis.com/auth/fitness.body_temperature.read+https://www.googleapis.com/auth/fitness.heart_rate.read+https://www.googleapis.com/auth/fitness.sleep.read&access_type=offline&include_granted_scopes=true&state=state_parameter_passthrough_value&redirect_uri=${process.env.SSO_BASE_URL}/mycircles&response_type=code&client_id=${this.client_id}`
      // https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/fitness.activity.read+https://www.googleapis.com/auth/fitness.body.read+https://www.googleapis.com/auth/fitness.body_temperature.read+https://www.googleapis.com/auth/fitness.heart_rate.read+https://www.googleapis.com/auth/fitness.sleep.read&access_type=offline&include_granted_scopes=true&state=state_parameter_passthrough_value&redirect_uri=https://staging.cluster.dovemed.com/mycircles&response_type=code&client_id=101951622305-r97l0t8dvq817olgmshsrjrekmuna8k1.apps.googleusercontent.com
    },
    async getToken() {
      try {
        const local_auth_code = JSON.parse(localStorage.getItem("gFitInfo")).auth_code
        const tokenRes = await axios.post("https://oauth2.googleapis.com/token", {
          client_id: this.client_id,
          client_secret: this.client_secret,
          code: local_auth_code,
          grant_type: "authorization_code",
          redirect_uri: `${process.env.SSO_BASE_URL}/mycircles`,
        })
        // console.log("token ->", tokenRes.data)
        this.a_token = tokenRes.data.access_token
      } catch (e) {
        // console.log(e)
      }
    },
    async getGFit(dataTypeName) {
      const today = new Date()
      try {
        const response = await axios.get("https://www.googleapis.com/fitness/v1/users/me/dataSources", {
          headers: { Authorization: `Bearer ${this.a_token}`, "Content-Type": "application/json" },
          params: {
            dataTypeName: dataTypeName,
            // startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2).toISOString(),
            // endTime: today.toISOString(),
          },
        })
        // console.log("Fit data:", response.data)
        response.data.dataSource.forEach(async element => {
          // const newRes = await axios.get(`https://www.googleapis.com/fitness/v1/users/me/dataSources/${element.dataStreamId}`, { headers: { Authorization: `Bearer ${this.a_token}` } })
          if (element.dataStreamName === "user_input" || element.dataType.name === "com.google.sleep.segment") {
            const newRes = await axios.get(
              `https://www.googleapis.com/fitness/v1/users/me/dataSources/${element.dataStreamId}/datasets/${new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2).getTime() *
                1e6}-${today.getTime() * 1e6}`,
              { headers: { Authorization: `Bearer ${this.a_token}` } }
            )
            console.log("newRes.data.point:", newRes.data.point)
            this.saveFitValue(element.dataType.name, newRes.data.point.length ? newRes.data.point[0].value[0].fpVal : undefined)
            // try {
            //   const testRes = await axios.get(`https://www.googleapis.com/fitness/v1/users/me/dataSources/${element.dataStreamId}/datasets/`, { headers: { Authorization: `Bearer ${this.a_token}` } })
            //   console.log("testRes:", testRes)
            // } catch (e) {
            //   console.log(element.dataStreamId, "-- ERROR -- testRes")
            // }
            // try {
            //   const testRes = await axios.get(`https://www.googleapis.com/fitness/v1/users/me/dataSources/${element.dataStreamId}/dataset:aggregate`, {
            //     headers: { Authorization: `Bearer ${this.a_token}` },
            //   })
            //   console.log("dataset:aggregate:", testRes)
            // } catch (e) {
            //   console.log(element.dataStreamId, "-- ERROR -- dataset:aggregate")
            // }
            // try {
            //   const testRes = await axios.get(`https://www.googleapis.com/fitness/v1/users/me/dataSources/${element.dataStreamId}/datasets:aggregate`, {
            //     headers: { Authorization: `Bearer ${this.a_token}` },
            //   })
            //   console.log("datasets:aggregate:", testRes)
            // } catch (e) {
            //   console.log(element.dataStreamId, "-- ERROR -- datasets:aggregate")
            // }
            // console.log(element.dataType.name, "---", newRes.data)
          }
        })
      } catch (error) {
        console.error("Error retrieving Fit data:", error)
      }
    },
    saveFitValue(type, value = 0) {
      switch (type) {
        case "sleep":
        case "com.google.sleep.segment":
          this.inputValues = this.inputValues.map(element => {
            if (element.id === "sleep") {
              return { ...element, val: +value.toFixed(2) }
            } else {
              return element
            }
          })
          break
        case "heart rate":
        case "com.google.heart_rate.bpm":
          this.inputValues = this.inputValues.map(element => {
            if (element.id === "heart_rate") {
              return { ...element, val: +value.toFixed(2) }
            } else {
              return element
            }
          })
          break
        case "weight":
        case "com.google.weight":
          this.inputValues = this.inputValues.map(element => {
            if (element.id === "weight") {
              return { ...element, val: +(value / 0.45359237).toFixed(2) }
            } else {
              return element
            }
          })
          break
        case "body temperature":
        case "com.google.body.temperature":
          this.inputValues = this.inputValues.map(element => {
            if (element.id === "temperature") {
              return { ...element, val: +((value * 9) / 5 + 32).toFixed(2) }
            } else {
              return element
            }
          })
          break
      }
    },
    // async getGFitDATASET(dataTypeName) {
    //   const today = new Date()
    //   try {
    //     const response = await axios.get("https://www.googleapis.com/fitness/v1/users/me/dataset", {
    //       headers: {
    //         Authorization: `Bearer ${this.a_token}`,
    //         "Content-Type": "application/json",
    //       },
    //       params: {
    //         dataTypeName: dataTypeName,
    //         startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2).toISOString(),
    //         endTime: today.toISOString(),
    //       },
    //     })
    //     const fitData = response.data
    //     console.log("DATASET - Fit data:", fitData)
    //   } catch (error) {
    //     console.error("Error retrieving Fit data:", error)
    //   }
    // },
    // async apiCall(api) {
    //   try {
    //     const response = await axios.get(api, {
    //       headers: {
    //         Authorization: `Bearer ${this.a_token}`,
    //         "Content-Type": "application/json",
    //       },
    //     })
    //     const fitData = response.data
    //     console.log("Fit data:", fitData)
    //   } catch (error) {
    //     console.error("Error retrieving Fit data:", error)
    //   }
    // },
    getList(name) {
      switch (name) {
        case "complimentary therapies":
          return this.CTs
        case "activities":
          return this.activities
        case "medications":
          return this.medications
        case "nutritions":
          return this.nutritions
        default:
          return []
      }
    },
    getLoader(name) {
      switch (name) {
        case "complimentary therapies":
          return this.ctLoading
        case "activities":
          return this.actLoading
        case "medications":
          return this.medLoading
        case "nutritions":
          return this.nutLoading
        default:
          return false
      }
    },
    handleDeleteMed(n, i) {
      switch (n) {
        case "procedures":
          this.procedures = this.procedures.filter(m => m.id !== i)
          break
        case "complimentary therapies":
          this.CTs = this.CTs.filter(m => m.id !== i)
          break
        case "activities":
          this.activities = this.activities.filter(m => m.id !== i)
          break
        case "medications":
          this.medications = this.medications.filter(m => m.id !== i)
          break
        case "nutritions":
          this.nutritions = this.nutritions.filter(m => m.id !== i)
          break
        // default:
        //   this.modalValues = this.modalValues.map(eachType => {
        //     if (n === eachType.name) {
        //       console.log("val:", eachType.val)
        //       return {
        //         ...eachType,
        //         val: eachType.val.filter(m => m.id !== i),
        //       }
        //     } else {
        //       return eachType
        //     }
        //   })
      }
    },
    getMoodColor(mood) {
      let bgC
      let bgH
      switch (mood) {
        case "good":
          bgC = "#00C8BC"
          bgH = "#B3EDDB"
          break
        case "okay":
          bgC = "#F5A623"
          bgH = "#FFF0D6"
          break
        case "bad":
          bgC = "#F58956"
          bgH = "#FFD7B3"
          break
        case "very good":
          bgC = "#00C853"
          bgH = "#D8F5D7"
          break
        case "very bad":
          bgC = "#EA3100"
          bgH = "#FFD7CC"
          break
      }
      if (mood !== this.clickedMood) {
        bgC = "#e2e8f0"
      } else {
        bgH = bgC
      }
      return {
        "--color": bgC,
        "--color-hover": bgH,
      }
    },
    async saveBtn(name, val = 0) {
      try {
        if (name === "mood" && this.moodDisabled) {
          this.$notification.error({ message: "Error", description: "You have already updated your Mood for today." })
          return
        }
        const payload = name === "mood" ? { mood_description: val || "", mood: this.clickedMood } : { [name.toLowerCase()]: val }
        await UserRepository.updateDovedaily(this.currentCircle.id, payload)
        if (!val.includes("Partially")) {
          this.$notification.success({ message: "Success", description: "Dovedaily Updated" })
        }
        if (name === "mood") {
          const payload = {
            user_circle: this.currentCircle.id,
            user: this.authUser.user.id,
            day_type: this.clickedMood,
            celebration_type: null,
            receiver_user: null,
            post_attach: [],
            latlong: null,
            post_text: val,
            post_title: "",
            post_time: new Date().toISOString(),
            tags: [],
          }
          await PostRepository.createPost(payload)
          this.moodDisabled = true
          // this.$notification.success({ message: "Success", description: "Post created successfully." })
        }
      } catch (e) {
        this.$notification.error({ message: "Error", description: "Could not update dovedaily. Try again" })
      }
    },
    handleOpenModal(name = "medication", action = "add", oldValue = {}) {
      switch (name.toLowerCase()) {
        case "procedures":
        case "procedure":
          this.$refs.ProcedureModal.show(action, oldValue)
          break
        case "medications":
        case "medication":
          this.$refs.MedicationModal.show(action, oldValue)
          break
        case "activities":
        case "activitys":
        case "activity":
          this.$refs.ActivityModal.show(action, oldValue)
          break
        case "nonmedicaltreatment":
        case "nonmedicaltreatments":
        case "therapy":
        case "therapys":
        case "therapies":
        case "complimentarytherapy":
        case "complimentarytherapys":
        case "complimentarytherapies":
        case "complimentary therapy":
        case "complimentary therapys":
        case "complimentary therapies":
          this.$refs.ComplimentaryTherapyModal.show(action, oldValue)
          break
        case "nutritions":
        case "nutrition":
          this.$refs.NutritionModal.show(action, oldValue)
          break
      }
    },
    handleStopTakingModal(name, currOption, oldVals) {
      this.saveBtn(name, currOption)
      if (currOption.includes("Partially")) {
        this.$refs.StopTakingModal.show(name, oldVals)
      }
    },
    handleModal(name, action, val = {}) {
      this.$refs.ViewDetailsModal.hide()
      this.handleOpenModal(name, action, val)
    },
  },
}
</script>

<style lang="scss" scoped>
img {
  height: 32px;
  width: 32px;
}
p {
  margin-bottom: 0 !important;
}
.shadow {
  box-shadow: 0px 0px 9px rgba(0, 0, 0, 0.1);
}
.eyeIcon {
  transform: scale(1.5);
}
.plusIcon {
  transform: scale(1.5);
  margin-left: 16px;
}
.dovedailyInput {
  margin-right: 10px;
  max-width: 131px;
}
.eachMoodBtn {
  height: fit-content;
  background: var(--color);
  &:hover {
    background: var(--color-hover);
  }
}
.br6:hover {
  background: #cbd5e1 !important;
}
.dovedaily-textarea {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}
@media screen and (max-width: 600px) {
  .mx-auto {
    margin-left: 16px !important;
    margin-right: 16px !important;
  }
  .font36 {
    font-size: 32px !important;
  }
  .justify-between,
  .gap10 {
    flex-direction: column;
    div {
      justify-content: center !important;
    }
  }
  .br6 {
    font-weight: 700 !important;
    width: 100% !important;
  }
  .rounded {
    flex-direction: column;
    gap: 10px;
    .dovedailyInput {
      max-width: 100% !important;
      height: 48px !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/home/UpdateDoveDaily.vue